<template>
  <div class="container-max-width">
    <p class="accent title">
      {{ titleSetting }}
      <span class="dim">
        {{ textValueCurrent }}
        {{ userPreferences[props.preferenceTarget] }}%
      </span>
    </p>

    <div class="wrapper-control">
      <div class="wrapper-width">
        <button
          class="button-primary button-small button-rounded button-icon"
          :disabled="isDisabledButtonDecrease"
          @click="decreaseInput"
        >
          <IconMinus class="icon-smaller" />
        </button>
        <div class="input-with-unit">
          <InputNumberWithComma v-model="modelValue" :valueMin="inputMinimum" @update:model-value="handleUserInput" />
        </div>

        <button
          class="button-primary button-small button-rounded button-icon"
          :disabled="isDisabledButtonIncrease"
          @click="increaseInput"
        >
          <IconPlus class="icon-smaller" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';

import IconMinus from '@/components/icons/IconMinus.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import InputNumberWithComma from '@/components/shared/base/InputNumberWithComma.vue';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';

const props = withDefaults(
  defineProps<{
    isGsc?: boolean;
    isGlobal?: boolean;
    preferenceTarget: string;
    titleSetting: string;
    textValueCurrent: string;
  }>(),
  { isGsc: false, isGlobal: false }
);

const modelValue = defineModel<number>({ default: 95 });

const { t } = useI18n();
const toastStore = useToastStore();
const userStore = useUserStore();
const { userPreferences } = storeToRefs(userStore);

const inputIncrement = 5; // 너비 조절 단위

const inputMinimum = computed(() => {
  return (
    {
      screenMessageWidth: 20,
      subtitleWidth: 20,
    }[props.preferenceTarget] || 20
  );
});

const isDisabledButtonDecrease = computed(() => {
  return modelValue.value <= inputMinimum.value;
});

const isDisabledButtonIncrease = computed(() => {
  return modelValue.value >= 100;
});

const handleUserInput = (value) => {
  modelValue.value = value;
  debounceInput(value);
};

const debounceInput = debounce((value: number) => {
  if (modelValue.value < inputMinimum.value) {
    toastStore.warn(t('screen_width_adjust_fail', { inputMinimum: inputMinimum.value }));

    return;
  }

  updatePreferences(value);
}, 800);

const updatePreferences = (value: number) => {
  if (props.isGlobal) {
    userStore.updateUserPreferences({
      setting: props.preferenceTarget,
      newVal: value,
    });
  }
};

const increaseInput = () => {
  if (modelValue.value < 100) {
    let newValue = modelValue.value + inputIncrement;
    modelValue.value = newValue;
    updatePreferences(newValue);
  }
};

const decreaseInput = () => {
  const newValue = modelValue.value - inputIncrement;

  if (newValue < inputMinimum.value) {
    modelValue.value = inputMinimum.value;
    return;
  } else {
    modelValue.value = newValue;
  }

  updatePreferences(newValue);
};
</script>

<style scoped>
.container-max-width {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  gap: 0.5rem;
}

.title {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.container-control {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.wrapper-width {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

input[type='number'] {
  width: 100%;
}

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

.input-with-unit {
  position: relative;
  display: inline-block;
}

.input-with-unit input {
  padding-right: 2.5rem;
}

.input-with-unit::after {
  content: '%';
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--font-dim);
  pointer-events: none;
}
</style>
